// What the public landing says, written for readers that do not run
// JavaScript: AI assistants, link previews and search crawlers on their first
// pass. vite.config.ts calls these at build time with the content saved in
// src/generated/site-snapshot.json and writes the result into index.html;
// LandingPage.tsx calls buildSiteGraph with the live content after it loads.
//
// No imports on purpose: the same file runs in the browser, inside the Vite
// build and under `node --test` (scripts/__tests__).

export const SITE_URL = 'https://www.caritaslaesperanza.com'
const ORGANIZATION_ID = `${SITE_URL}/#organization`
const CENTRE_ADDRESS = 'Alborada 430, Parques del Pedregal, Tlalpán, 14010, CDMX'
const TIME_ZONE = 'America/Mexico_City'

// ============ Content shape (what GET /api/site/public returns) ============

export interface PrerenderContent {
  hero_title?: string | null
  hero_subtitle?: string | null
  hero_image_url?: string | null
  about_title?: string | null
  about_body?: string | null
  contact_address?: string | null
  contact_phone?: string | null
  contact_email?: string | null
  social_facebook?: string | null
  social_instagram?: string | null
  social_twitter?: string | null
  parroquia_url?: string | null
  mass_section_title?: string | null
  mass_section_subtitle?: string | null
  hours_section_title?: string | null
  hours_section_subtitle?: string | null
}

export interface PrerenderEvent {
  title: string
  body?: string | null
  cover_image_url?: string | null
  event_date?: string | null
  location?: string | null
}

export interface PrerenderPost {
  title: string
  excerpt?: string | null
  cover_image_url?: string | null
  published_at?: string | null
}

export interface PrerenderFaq {
  question: string
  answer: string
}

export interface PrerenderMass {
  kind: 'semanal' | 'especial'
  day_of_week?: number | null
  specific_date?: string | null
  time: string
  description?: string | null
}

export interface PrerenderHour {
  day_of_week: number
  opens_at: string
  closes_at: string
  display_order?: number
}

export interface PrerenderSiteData {
  content: PrerenderContent | null
  events: PrerenderEvent[]
  posts: PrerenderPost[]
  faqs: PrerenderFaq[]
  masses: PrerenderMass[]
  hours: PrerenderHour[]
}

// ============ Formatting shared with the page ============

// Monday is 1 and Sunday is 7, as stored by the content editor
export const DAY_LABELS = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']
const SCHEMA_DAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export const formatTime12h = (t: string): string => {
  const m = t.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return t
  let h = parseInt(m[1], 10)
  const min = m[2]
  const ampm = h >= 12 ? 'pm' : 'am'
  h = h % 12; if (h === 0) h = 12
  // Non-breaking space: "8:00 am" must never wrap between number and meridiem.
  return `${h}:${min}\u00a0${ampm}`
}

/** "24 de junio de 2026", in Mexico City time whatever the zone of the machine building the site. */
export function formatDateMx(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', timeZone: TIME_ZONE })
}

/** "8:00 am, 12:00 pm y 7:00 pm" */
function joinSpanish(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`
}

function groupByDay<T>(items: T[], dayOf: (item: T) => number | null | undefined): Map<number, T[]> {
  const byDay = new Map<number, T[]>()
  for (const item of items) {
    const day = dayOf(item)
    if (!day || day < 1 || day > 7) continue
    byDay.set(day, [...(byDay.get(day) ?? []), item])
  }
  return byDay
}

function sortedHours(hours: PrerenderHour[]): PrerenderHour[] {
  // Same order as the page: the editor's order first, then the opening time
  return [...hours].sort((a, b) =>
    (a.display_order ?? 0) - (b.display_order ?? 0) || a.opens_at.localeCompare(b.opens_at))
}

// ============ Plain text block ============

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

/** Escaped text with its line breaks kept, the way the page shows it (white-space: pre-wrap). */
const paragraphs = (value: string): string =>
  value
    .split(/\n{2,}/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => `<p>${escapeHtml(part).replace(/\n/g, '<br>')}</p>`)
    .join('')

const sentence = (value: string): string => (/[.!?…:]$/.test(value.trim()) ? value.trim() : `${value.trim()}.`)

export const STATIC_TEXT_ID = 'contenido-estatico'

/**
 * The landing's content as plain HTML, in the order the page shows it. It sits
 * inside #root, hidden from anyone with JavaScript (React empties #root when it
 * mounts) and shown by a <noscript> style to anyone without it.
 */
export function renderSiteText(data: PrerenderSiteData): string {
  const c = data.content ?? {}
  const out: string[] = []
  const section = (id: string, title: string, body: string) => {
    if (body) out.push(`<section id="${id}-texto"><h2>${escapeHtml(title)}</h2>${body}</section>`)
  }

  out.push(`<h1>${escapeHtml(c.hero_title || 'Cáritas La Esperanza')}</h1>`)
  if (c.hero_subtitle) out.push(`<p>${escapeHtml(c.hero_subtitle)}</p>`)

  if (c.about_body) section('acerca', c.about_title || 'Quiénes somos', paragraphs(c.about_body))

  if (data.hours.length > 0) {
    const byDay = groupByDay(sortedHours(data.hours), h => h.day_of_week)
    const rows = [1, 2, 3, 4, 5, 6, 7].map(day => {
      const ranges = byDay.get(day) ?? []
      const text = ranges.length === 0
        ? 'cerrado'
        : joinSpanish(ranges.map(r => `de ${formatTime12h(r.opens_at)} a ${formatTime12h(r.closes_at)}`))
      return `<li>${DAY_LABELS[day]}: ${escapeHtml(text)}.</li>`
    })
    section('horarios', c.hours_section_title || 'Horarios de operación',
      `${c.hours_section_subtitle ? `<p>${escapeHtml(c.hours_section_subtitle)}</p>` : ''}` +
      `<p>Horario de atención del centro comunitario:</p><ul>${rows.join('')}</ul>`)
  }

  const weekly = data.masses.filter(m => m.kind === 'semanal')
  const special = data.masses.filter(m => m.kind === 'especial')
  if (weekly.length > 0 || special.length > 0) {
    let body = c.mass_section_subtitle ? `<p>${escapeHtml(c.mass_section_subtitle)}</p>` : ''
    if (weekly.length > 0) {
      const byDay = groupByDay(weekly, m => m.day_of_week)
      const rows = [1, 2, 3, 4, 5, 6, 7].filter(day => byDay.has(day)).map(day => {
        const times = [...(byDay.get(day) ?? [])]
          .sort((a, b) => a.time.localeCompare(b.time))
          .map(m => formatTime12h(m.time) + (m.description?.trim() ? ` (${m.description.trim()})` : ''))
        return `<li>${DAY_LABELS[day]}: ${escapeHtml(joinSpanish(times))}.</li>`
      })
      body += `<h3>Horarios semanales de misa</h3><ul>${rows.join('')}</ul>`
    }
    if (special.length > 0) {
      const rows = special.map(m => {
        const when = [formatDateMx(m.specific_date), formatTime12h(m.time)].filter(Boolean).join(', ')
        return `<li>${escapeHtml(sentence(when + (m.description?.trim() ? `: ${m.description.trim()}` : '')))}</li>`
      })
      body += `<h3>Celebraciones especiales</h3><ul>${rows.join('')}</ul>`
    }
    section('misas', c.mass_section_title || 'Misas y horarios', body)
  }

  if (data.events.length > 0) {
    section('eventos', 'Eventos y actividades', data.events.map(ev => {
      const facts = [
        ev.event_date ? `Fecha: ${formatDateMx(ev.event_date)}.` : '',
        ev.location ? `Lugar: ${sentence(ev.location)}` : '',
      ].filter(Boolean).join(' ')
      return `<article><h3>${escapeHtml(ev.title)}</h3>` +
        (facts ? `<p>${escapeHtml(facts)}</p>` : '') +
        (ev.body ? paragraphs(ev.body) : '') +
        '</article>'
    }).join(''))
  }

  if (data.posts.length > 0) {
    section('blog', 'Blog', data.posts.map(p =>
      `<article><h3>${escapeHtml(p.title)}</h3>` +
      (p.published_at ? `<p>Publicado el ${escapeHtml(formatDateMx(p.published_at))}.</p>` : '') +
      (p.excerpt ? paragraphs(p.excerpt) : '') +
      '</article>').join(''))
  }

  if (data.faqs.length > 0) {
    section('faq', 'Preguntas frecuentes',
      data.faqs.map(f => `<h3>${escapeHtml(f.question)}</h3>${paragraphs(f.answer)}`).join(''))
  }

  const contact = [
    c.contact_address ? `<li>Dirección: ${escapeHtml(c.contact_address)}</li>` : '',
    c.contact_phone ? `<li>WhatsApp: ${escapeHtml(c.contact_phone)}</li>` : '',
    c.contact_email ? `<li>Correo: <a href="mailto:${escapeHtml(c.contact_email)}">${escapeHtml(c.contact_email)}</a></li>` : '',
    c.social_facebook ? `<li>Facebook: <a href="${escapeHtml(c.social_facebook)}">${escapeHtml(c.social_facebook)}</a></li>` : '',
  ].join('')
  section('contacto', 'Contacto', contact ? `<ul>${contact}</ul>` : '')

  return `<div id="${STATIC_TEXT_ID}">${out.join('')}</div>`
}

// ============ JSON-LD ============

/**
 * The schema.org graph for the landing: the website, the centre (NGO and
 * LocalBusiness, with its opening hours), the FAQ, the events and the posts.
 * Every fact comes from the content; nothing is invented here.
 */
export function buildSiteGraph(data: PrerenderSiteData): Record<string, unknown> {
  const c = data.content ?? {}
  const sameAs = [c.social_facebook, c.social_instagram, c.social_twitter, c.parroquia_url]
    .filter((url): url is string => typeof url === 'string' && url.length > 0)

  const organization: Record<string, unknown> = {
    '@type': ['NGO', 'LocalBusiness'],
    '@id': ORGANIZATION_ID,
    name: 'Centro Comunitario Cáritas La Esperanza',
    alternateName: 'Cáritas La Esperanza',
    url: SITE_URL,
    logo: `${SITE_URL}/logocaritas.png`,
    image: c.hero_image_url || `${SITE_URL}/og-image.png`,
    description: c.hero_subtitle || 'Centro comunitario al servicio de la salud y la dignidad humana en CDMX.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Alborada 430, Parques del Pedregal',
      addressLocality: 'Tlalpán',
      addressRegion: 'CDMX',
      postalCode: '14010',
      addressCountry: 'MX',
    },
    areaServed: { '@type': 'City', name: 'Ciudad de México' },
    ...(sameAs.length > 0 && { sameAs }),
    ...(c.contact_phone && { telephone: c.contact_phone }),
    ...(c.contact_email && { email: c.contact_email }),
  }
  if (data.hours.length > 0) {
    organization.openingHoursSpecification = sortedHours(data.hours)
      .filter(h => h.day_of_week >= 1 && h.day_of_week <= 7)
      .sort((a, b) => a.day_of_week - b.day_of_week)
      .map(h => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: SCHEMA_DAYS[h.day_of_week],
        opens: h.opens_at,
        closes: h.closes_at,
      }))
  }

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'Cáritas La Esperanza',
      inLanguage: 'es-MX',
      publisher: { '@id': ORGANIZATION_ID },
    },
    organization,
  ]

  if (data.faqs.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/#faq`,
      mainEntity: data.faqs.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    })
  }

  for (const ev of data.events) {
    graph.push({
      '@type': 'Event',
      name: ev.title,
      ...(ev.event_date && { startDate: ev.event_date }),
      ...(ev.body && { description: ev.body }),
      ...(ev.cover_image_url && { image: ev.cover_image_url }),
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      organizer: { '@id': ORGANIZATION_ID },
      location: { '@type': 'Place', name: ev.location || 'Cáritas La Esperanza', address: CENTRE_ADDRESS },
    })
  }

  for (const p of data.posts) {
    graph.push({
      '@type': 'BlogPosting',
      headline: p.title,
      ...(p.excerpt && { description: p.excerpt }),
      ...(p.cover_image_url && { image: p.cover_image_url }),
      ...(p.published_at && { datePublished: p.published_at }),
      author: { '@id': ORGANIZATION_ID },
      publisher: { '@id': ORGANIZATION_ID },
      mainEntityOfPage: `${SITE_URL}/#blog`,
    })
  }

  return { '@context': 'https://schema.org', '@graph': graph }
}

/** JSON for a <script> element: "<" is escaped so a text containing "</script>" cannot end the element early. */
export const jsonForScript = (value: unknown): string => JSON.stringify(value).replace(/</g, '\\u003c')
